const LS_BUILD_HASH = 'hs.ui.buildHash';
const LS_DEFER_UNTIL = 'hs.ui.deferUpdateUntil';

type OnUpdate = () => void;
let timer: number | undefined;

interface VersionPayload {
  buildTime?: string;
  buildVersion?: string;
  commit?: string;
}

function readMetaBuildVersion(): string | undefined {
  const meta = document.querySelector('meta[name="app-build-hash"]') as HTMLMetaElement | null;
  return meta?.content || undefined;
}

function getCurrentVersion(): string {
  // Prefer injected build version from Vite define
  const injected = (typeof __BUILD_INFO__ !== 'undefined' && __BUILD_INFO__?.buildVersion) as string | undefined;
  if (injected && injected.length > 0) return injected;

  // Fallback to meta tag if provided
  const meta = readMetaBuildVersion();
  if (meta && meta.length > 0) return meta;

  // Last resort: build time (still changes between builds)
  const time = (typeof __BUILD_INFO__ !== 'undefined' && __BUILD_INFO__?.buildTime) as string | undefined;
  return time || '';
}

async function fetchServerVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const json = (await res.json()) as Partial<VersionPayload>;
    const v = json.buildVersion || json.commit || null;
    return typeof v === 'string' ? v : null;
  } catch {
    return null;
  }
}

export function markVersionAsSeen(version?: string): void {
  const v = version ?? getCurrentVersion();
  localStorage.setItem(LS_BUILD_HASH, v);
}

export function readDeferUntil(): number {
  const v = localStorage.getItem(LS_DEFER_UNTIL);
  return v ? parseInt(v, 10) : 0;
}

export function writeDeferFor(minutes: number): void {
  const until = Date.now() + minutes * 60_000;
  localStorage.setItem(LS_DEFER_UNTIL, until.toString());
}

export function getPollIntervalMs(): number {
  const v = parseInt(import.meta.env.VITE_VERSION_CHECK_INTERVAL_MS as string, 10);
  return Number.isFinite(v) && v > 0 ? v : 300_000; // Default: 5 minutes
}

export function startVersionWatcher(onUpdate: OnUpdate) {
  stopVersionWatcher();

  // Initialize stored version if missing
  const stored = localStorage.getItem(LS_BUILD_HASH);
  if (!stored) {
    localStorage.setItem(LS_BUILD_HASH, getCurrentVersion());
  }

  const tick = async () => {
    // Respect defer window
    const deferUntil = readDeferUntil();
    if (deferUntil && Date.now() < deferUntil) return;

    const current = getCurrentVersion();
    const server = await fetchServerVersion();

    if (!server) return; // Can't determine server version

    // Store current if changed locally (e.g., hot reload in dev)
    const localStored = localStorage.getItem(LS_BUILD_HASH);
    if (localStored !== current) {
      localStorage.setItem(LS_BUILD_HASH, current);
    }

    // Trigger when server version differs from current
    if (server && server !== current) {
      onUpdate();
    }
  };

  // Immediate check, then interval
  tick();
  timer = window.setInterval(() => void tick(), getPollIntervalMs());
}

export function stopVersionWatcher() {
  if (timer) {
    clearInterval(timer);
    timer = undefined;
  }
}
